import { memo, useEffect, useMemo, useState } from "react";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { Box, Checkbox, FormControlLabel, FormGroup, Paper, Radio, RadioGroup, Stack, Typography, useTheme } from "@mui/material";
import { StepSlider } from "components/StepSlider";
import { NumberField } from "components/NumberField";
import { Label } from "components/Label";
import {
  IOptimizationConfig,
  IOptimizationConfigCallBack,
  OptimizationTypeEnum,
  IRoadwayConfig,
  ILuminaireLayoutConfig,
  ILightingClassConfig,
  TooltipData,
} from "types";
import { APP_OPTIMIZATION_HEIGHT_LIGHT_POINT_STEP, APP_OPTIMIZATION_TILT_OF_LAMINAR_STEP } from "consts";
import { OPTIMIZATION_BOUNDS } from "./consts";
import { useOptimizationHandlers } from "./useOptimizationHandlers";
import getStyles from "./styles";

interface IProps {
  optimizationConfig: IOptimizationConfig;
  optimizationConfigCallBack: IOptimizationConfigCallBack;
  roadwayConfig: IRoadwayConfig;
  luminaireLayoutConfig: ILuminaireLayoutConfig;
  lightingClassConfig: ILightingClassConfig;
  tooltips: TooltipData | null;
}

const Optimization: FC<IProps> = ({ optimizationConfig, optimizationConfigCallBack, roadwayConfig, lightingClassConfig, luminaireLayoutConfig, tooltips }) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const { maintenanceFactorHandler, checkboxHandler, radioButtonHandler, selectWithStepsHandler } = useOptimizationHandlers({
    optimizationConfig,
    optimizationConfigCallBack,
    roadwayConfig,
    luminaireLayoutConfig,
  });

  const {
    optimizationType,
    ignoreTi,
    ignoreRei,
    distanceBetweenPoles,
    shouldOptimizeDistanceBetweenPoles,
    shouldOptimizeHeightOfTheLightPoint,
    shouldOptimizeOverhang,
    shouldOptimizeTiltOfTheLuminaire,
    heightOfTheLightPoint,
    overhang,
    tiltOfTheLuminaire,
    maintenanceFactor,
  } = optimizationConfig;

  const { luminaireConfig } = luminaireLayoutConfig;

  const showMClass = useMemo(
    () => lightingClassConfig?.roadClasses?.includes("M") || lightingClassConfig?.customClasses?.mainCustomClass?.class_type?.includes("M"),
    [lightingClassConfig]
  );

  const styles = useMemo(() => getStyles(theme), [theme]);

  return (
    <Stack sx={styles.container}>
      <Typography variant="h2" sx={styles.title}>
        {t("Optimization")}
      </Typography>

      <Paper sx={styles.optimization}>
        <Box component="section" sx={styles.section}>
          <Label
            variant="h4"
            text={t("Type")}
            help={
              tooltips?.optimization_type ??
              t(
                "There are two options for the optimization: Make the distance between the poles as far as possible. Or make the energy consumption as low as possible."
              )
            }
          />
          <RadioGroup
            name="optimizationType"
            value={optimizationType}
            onChange={(_, value) => radioButtonHandler(value as OptimizationTypeEnum)}
            sx={styles.group}
          >
            <FormControlLabel
              control={<Radio />}
              value={OptimizationTypeEnum.MAXIMUM_DISTANCE}
              label={t("Optimize for maximum distance between poles")}
              sx={styles.control}
            />
            <FormControlLabel
              control={<Radio />}
              value={OptimizationTypeEnum.LOWEST_CONSUMPTION}
              label={t("Optimize for lowest energy consumption")}
              sx={styles.control}
            />
          </RadioGroup>
        </Box>

        {showMClass && (
          <Box component="section" sx={styles.section}>
            <Label
              variant="h4"
              text={t("Ignore")}
              help={
                tooltips?.optimization_ignore ??
                t(
                  "It is possible to exclude two parameters from the optimization: The glare value Ti and the edge illuminance ratio Rei. This allows more results."
                )
              }
            />
            <FormGroup sx={styles.group}>
              <FormControlLabel
                control={<Checkbox name="ignore_ti" checked={ignoreTi} onChange={() => checkboxHandler("ignore_ti", !ignoreTi)} />}
                label={t("Ignore Ti")}
                sx={styles.control}
              />
              <FormControlLabel
                control={<Checkbox name="ignore_rei" checked={ignoreRei} onChange={() => checkboxHandler("ignore_rei", !ignoreRei)} />}
                label={t("Ignore Rei")}
                sx={styles.control}
              />
            </FormGroup>
          </Box>
        )}
      </Paper>

      <Typography variant="h3" sx={styles.subtitle}>
        {t("Luminaire row")}
      </Typography>

      <StepSlider
        name="distanceBetweenPoles"
        label={t("Distance between poles")}
        data={distanceBetweenPoles}
        optimize={shouldOptimizeDistanceBetweenPoles}
        tooltip={
          tooltips?.optimization_dbp ??
          t(
            "With the range option is switched on: Set the minimum and maximum distance between the poles and the step width for the optimization. By switching the range option off you can define a fixed pole distance and take the pole distance off the optimization."
          )
        }
        min={OPTIMIZATION_BOUNDS.distanceBetweenPoles.min}
        max={OPTIMIZATION_BOUNDS.distanceBetweenPoles.max}
        step={OPTIMIZATION_BOUNDS.distanceBetweenPoles.step}
        unit="m"
        onChange={selectWithStepsHandler}
        onSelect={checkboxHandler}
      />

      <StepSlider
        name="heightOfTheLightPoint"
        label={t("Height of the light point")}
        data={heightOfTheLightPoint}
        optimize={shouldOptimizeHeightOfTheLightPoint}
        tooltip={
          tooltips?.optimization_holp ??
          t(
            "With the range option is switched on: Set the minimum and maximum height of the luminaire and the step width for the optimization. By switching the range option off you can define a fixed pole height and take the pole height off the optimization."
          )
        }
        min={OPTIMIZATION_BOUNDS.heightOfTheLightPoint.min}
        max={OPTIMIZATION_BOUNDS.heightOfTheLightPoint.max}
        step={APP_OPTIMIZATION_HEIGHT_LIGHT_POINT_STEP || 0.25}
        unit="m"
        onChange={selectWithStepsHandler}
        onSelect={checkboxHandler}
      />

      {luminaireConfig !== "Rope" && (
        <>
          <StepSlider
            name="overhang"
            label={t("Overhang")}
            data={overhang}
            optimize={shouldOptimizeOverhang}
            tooltip={
              tooltips?.optimization_overhang ??
              t(
                "With the range option is switched on: Set the minimum and maximum overhang of the luminaire and the step width for the optimization. By switching the range option off you can define a fixed luminaire overhang and take the overhang off the optimization. An overhang of 0 means a luminaire direct on the pole."
              )
            }
            min={OPTIMIZATION_BOUNDS.overhang.min}
            max={OPTIMIZATION_BOUNDS.overhang.max}
            step={OPTIMIZATION_BOUNDS.overhang.step}
            unit="m"
            onChange={selectWithStepsHandler}
            onSelect={checkboxHandler}
          />

          <StepSlider
            name="tiltOfTheLuminaire"
            label={t("Tilt of the luminaire")}
            data={tiltOfTheLuminaire}
            optimize={shouldOptimizeTiltOfTheLuminaire}
            tooltip={
              tooltips?.optimization_totl ??
              t(
                "With the range option is switched on: Set the minimum and maximum tilt of the luminaire and the step width for the optimization. By switching the range option off you can define a fixed tilt and take the tilt off the optimization."
              )
            }
            min={OPTIMIZATION_BOUNDS.tiltOfTheLuminaire.min}
            max={OPTIMIZATION_BOUNDS.tiltOfTheLuminaire.max}
            step={APP_OPTIMIZATION_TILT_OF_LAMINAR_STEP || 2.5}
            unit="°"
            onChange={selectWithStepsHandler}
            onSelect={checkboxHandler}
          />
        </>
      )}

      <Paper sx={styles.maintenance}>
        <NumberField
          name="maintenanceFactor"
          value={maintenanceFactor}
          label={t("Maintenance factor:")}
          tooltip={
            tooltips?.optimization_mf ??
            t(
              "The Maintenance factor reflects the loss of light output of the luminaire installation over the time. A 1.0 would mean there is no loss of light output over the time. 1.0 is the new installed situation."
            )
          }
          min={OPTIMIZATION_BOUNDS.maintenanceFactor.min}
          max={OPTIMIZATION_BOUNDS.maintenanceFactor.max}
          precision={OPTIMIZATION_BOUNDS.maintenanceFactor.precision}
          step={OPTIMIZATION_BOUNDS.maintenanceFactor.step}
          helperText={t("validation_sentence", { min: OPTIMIZATION_BOUNDS.maintenanceFactor.min, max: OPTIMIZATION_BOUNDS.maintenanceFactor.max })}
          onChange={maintenanceFactorHandler}
        />
      </Paper>
    </Stack>
  );
};

Optimization.displayName = "Optimization";

export default memo(Optimization);
